import { toPositiveOptionId } from '@/utils/optionValues';
import { buildFirstInstallmentOptions as buildSharedFirstInstallmentOptions } from '@/composables/shared/useFirstInstallmentOptions';
import { parseAmountInput } from '@/utils/numberValue';
import { buildTodayDate, formatDateForApi } from '@/utils/datePresentation';
import {
  CORE_STATUS_OPTIONS,
  CORE_FREQUENCY_OPTIONS,
} from '@/composables/shared/useCoreOptionSets';
import { TAX_MODE_OPTIONS } from '@/composables/shared/useTaxModeOptions';
import { PAYMENTS_TEXT } from '@/texts/catalog/payments';

const PLAN_MODAL_TEXT = PAYMENTS_TEXT.planModal;

export const PAYMENT_PLAN_MODAL_SECTIONS = [
  {
    key: 'general',
    title: PLAN_MODAL_TEXT.sections.general.title,
    description: PLAN_MODAL_TEXT.sections.general.description,
    icon: 'fa-file-text-o',
  },
  {
    key: 'billing',
    title: PLAN_MODAL_TEXT.sections.billing.title,
    description: PLAN_MODAL_TEXT.sections.billing.description,
    icon: 'fa-file-code-o',
  },
  {
    key: 'details',
    title: PLAN_MODAL_TEXT.sections.details.title,
    description: PLAN_MODAL_TEXT.sections.details.description,
    icon: 'fa-align-left',
  },
];

export const PAYMENT_PLAN_DOCUMENT_TYPE_OPTIONS = [
  { value: 'cfdi', label: PLAN_MODAL_TEXT.documentTypes.cfdi },
  { value: 'simple_note', label: PLAN_MODAL_TEXT.documentTypes.simpleNote },
];

const toNumber = (value) => parseAmountInput(value, 0);

const toDateString = (value) => formatDateForApi(value, { emptyValue: '', allowString: true });

export const buildFirstInstallmentOptions = (now = new Date()) => buildSharedFirstInstallmentOptions(now);

export const createInitialPaymentPlanForm = () => ({
  voucherType: 'cfdi',
  customer: null,
  currencyCode: 'MXN',
  exchangeRate: 1,
  amount: 0,
  withoutTaxes: false,
  taxType: 'individual',
  taxes: [],
  taxGroup: null,
  frequency: 'unique',
  paymentsCount: 1,
  firstInstallmentOption: null,
  periodicityOption: null,
  folio: '',
  concept: '',
  expenseDate: buildTodayDate(),
  paymentDate: buildTodayDate(),
  cfdiDate: buildTodayDate(),
  billingStatus: 'unbilled',
  orderlyInvoice: null,
  orderlyInvoiceId: null,
  externalInvoiceReference: '',
  invoiceXmlFile: null,
  area: null,
  comments: '',
});

export const createInitialPaymentPlanTaxPreview = () => ({
  amount: 0,
  discount: 0,
  subtotal: 0,
  calculation_type: 'from-subtotal',
  tax_type: 'individual',
  taxes: {
    transferred_total: 0,
    withheld_total: 0,
    local_transferred_total: 0,
    local_withheld_total: 0,
    net_total: 0,
    items: [],
    summary: [],
  },
  total: 0,
});

export const createInitialPaymentPlanModalOptions = () => ({
  documentTypes: PAYMENT_PLAN_DOCUMENT_TYPE_OPTIONS,
  expenseStatuses: CORE_STATUS_OPTIONS,
  frequencies: CORE_FREQUENCY_OPTIONS,
  taxModes: TAX_MODE_OPTIONS,
  customers: [],
  currencies: [],
  taxes: [],
  taxGroups: [],
  periodicities: [],
  areas: [],
  invoices: [],
});

export const buildPaymentPlanTaxPreviewPayload = (form = {}) => {
  const withoutTaxes = Boolean(form?.withoutTaxes);
  const taxMode = String(form?.taxType || 'individual').trim().toLowerCase() === 'group'
    ? 'group'
    : 'individual';

  const taxIds = (Array.isArray(form?.taxes) ? form.taxes : [])
    .map((item) => toPositiveOptionId(item))
    .filter((id) => id > 0);

  const taxGroupId = toPositiveOptionId(form?.taxGroup);

  return {
    amount: toNumber(form?.amount),
    discount: 0,
    calculation_type: 'from-subtotal',
    tax_type: withoutTaxes ? 'individual' : taxMode,
    tax_ids: withoutTaxes ? [] : (taxMode === 'individual' ? taxIds : []),
    tax_group_id: withoutTaxes ? 0 : (taxMode === 'group' ? taxGroupId : 0),
    local_taxes: [],
  };
};

export const buildPaymentPlanPayload = (form = {}, taxPreview = null, context = {}) => {
  const payloadPreview = taxPreview && typeof taxPreview === 'object'
    ? taxPreview
    : createInitialPaymentPlanTaxPreview();

  const withoutTaxes = Boolean(form?.withoutTaxes);
  const taxType = String(form?.taxType || 'individual').trim().toLowerCase();
  const saleId = Math.max(0, Number(context?.saleId || 0) || 0);
  const customerId = toPositiveOptionId(form?.customer);
  const voucherType = String(form?.voucherType || 'cfdi').trim().toLowerCase() === 'simple_note'
    ? 'simple_note'
    : 'cfdi';
  const billingStatus = String(form?.billingStatus || 'unbilled').trim().toLowerCase();
  const selectedInvoiceId = toPositiveOptionId(form?.orderlyInvoice);
  const invoiceId = Math.max(
    0,
    Number(selectedInvoiceId > 0 ? selectedInvoiceId : form?.orderlyInvoiceId || 0) || 0
  );
  const externalInvoiceReference = String(form?.externalInvoiceReference || '').trim();

  return {
    voucher_type: voucherType,
    special: voucherType === 'simple_note' ? 1 : 0,
    currency: String(form?.currencyCode || 'MXN').trim().toUpperCase(),
    exchange_rate: Number(form?.exchangeRate || 0) > 0 ? Number(form.exchangeRate) : 1,
    amount: toNumber(form?.amount),
    discount: 0,
    tax_type: withoutTaxes ? 'individual' : taxType,
    tax_ids: withoutTaxes
      ? []
      : (Array.isArray(form?.taxes) ? form.taxes : [])
        .map((item) => toPositiveOptionId(item))
        .filter((id) => id > 0),
    tax_group_id: withoutTaxes ? 0 : toPositiveOptionId(form?.taxGroup),
    frequency: String(form?.frequency || 'unique').trim().toLowerCase(),
    payments_count: Math.max(1, Number(form?.paymentsCount || 1) || 1),
    expenses_count: Math.max(1, Number(form?.paymentsCount || 1) || 1),
    first_installment: String(form?.firstInstallmentOption?.value || '').trim(),
    periodicity_code: String(form?.periodicityOption?.value || '').trim().toLowerCase(),
    folio: String(form?.folio || '').trim(),
    concept: String(form?.concept || '').trim(),
    date: toDateString(form?.expenseDate),
    payment_date: toDateString(form?.paymentDate),
    cfdi_date: toDateString(form?.cfdiDate),
    invoiced: billingStatus === 'unbilled' ? 0 : 1,
    invoice_id: billingStatus === 'orderly' ? invoiceId : 0,
    invoice_reference: billingStatus === 'external' ? externalInvoiceReference : '',
    sale_id: saleId,
    company_id: customerId,
    area_id: toPositiveOptionId(form?.area),
    comments: String(form?.comments || '').trim(),
    tax_preview: payloadPreview,
  };
};

